/**
 * Author: Satyam Sharma
 * Date: 07 January 2025
 * Description: This component, `Ai`, is designed to handle streaming AI responses in real-time. 
   It renders incoming messages with markdown support and dynamically updates based on user interactions.

   Key Features:
   - Integrates Markdown rendering using `markdown-it` for rich text formatting with secure links.
   - Handles user feedback on AI responses through thumbs-up and thumbs-down actions.
   - Maintains feedback state locally using `useState` and submits it to the server asynchronously.
   - Ensures feedback visibility and proper rendering of messages in a styled container.
   - Introduces speed handling for typing simulation and Web Speech API for text-to-speech functionality.
   - Web speech API is used to speak text being received in the streaming or directly.
 **/

import markdownIt from 'markdown-it';
import mila from "markdown-it-link-attributes";
import { useEffect, useState } from 'react';
import { saveMessageFeedback } from '../../../utilities/Response';
import { Feedback } from '../../../interfaces/Bot';
import { AiComponentProps } from '../../../interfaces/ai';
import { useDispatch, useSelector } from 'react-redux';
import { updateAiEnvironment, updateEnvironment } from '../../../actions/environment.action';
import AiThinking from '../../AIThinking';
import Spectrum from '../../Spectrum';
import { stopVoiceOutput } from '../../../services/shared';
import './index.css';


let md = markdownIt({ html: false, linkify: true, typographer: true, breaks: false });
md.use(mila, { attrs: { target: "_blank", rel: "noopener", } });

export const submitFeedback = (data: Feedback) => {
  saveMessageFeedback(data)
    .catch(error => console.log(error));
}

export default function Ai(props: AiComponentProps) {
  const { message, topMargin } = props;
  const environment = useSelector((state: any) => state.environment);
  const dispatch = useDispatch();

  const activeMID = environment?.activeMID;
  const [hasClickedThumbsUp, setHasClickedThumbsUp] = useState(props.hasClickedThumbsUp || false);
  const [hasClickedThumbsDown, setHasClickedThumbsDown] = useState(props.hasClickedThumbsUp || false);
  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [accumulatedText, setAccumulatedText] = useState('');
  const [spokenText, setSpokenText] = useState(''); // to keep track of spoken text
  const isVoiceSupportEnabled = environment?.chatMenu?.voiceSupport?.isEnabled;
  // const synth = useRef(window.speechSynthesis).current;
  // const [availableVoices, setAvailableVoices] = useState<SpeechSynthesisVoice[]>([]);

  // Constants
  const TYPING_SPEED = 200;
  // const SPEAKING_RATE = 1.0;

  /**
   * 1. Remove headings (###, ##, #)
   * 2. Remove bold (**text**)
   * 3. Remove italics (*text*)
   * 4. Remove underline (__text__)
   * 5. Remove inline code (`text`)
   * 6. Keep link text but remove URL
   * 7. Remove list bullets (- or *)
   * 8. Reduce excessive newlines
   */
  // const cleanMarkdown = (text: string) => {
  //   return text
  //     .replace(/#+\s?/g, '')
  //     .replace(/\*\*(.*?)\*\*/g, '$1')
  //     .replace(/\*(.*?)\*/g, '$1')
  //     .replace(/__(.*?)__/g, '$1')
  //     .replace(/`(.*?)`/g, '$1')
  //     .replace(/\[([^\]]+)\]\((.*?)\)/g, '$1')
  //     .replace(/[-*]\s?/g, '')
  //     .replace(/\n{2,}/g, '\n');
  // }
  /**
    * Author: Jay Tyagi
    * Date: 20 March 2025
    * Description: This function loads voices when the component mounts by storing them in the availableVoices state variable.
    * 
    * Satyam Sharma: Not Needed as of 17th May
  **/
  // useEffect(() => {
  //   const synth = window.speechSynthesis;
  //   function loadVoices() {
  //     const voices = synth.getVoices();
  //     if (voices.length > 0) {
  //       setAvailableVoices(voices);
  //     } else {
  //       setTimeout(() => loadVoices(), 100);
  //     }
  //   }
  //   /**
  //     In Google Chrome the voices are not ready on page load
  //   **/
  //   if (synth.onvoiceschanged !== undefined) {
  //     synth.onvoiceschanged = loadVoices;
  //   }
  //   loadVoices();
  // }, []);

  // const speakText = useCallback((text: string, rate: number) => {
  //   if (!text || availableVoices.length === 0) return;

  //   synth.cancel();

  //   const utterance = new SpeechSynthesisUtterance(cleanMarkdown(text));
  //   const voiceOptions = availableVoices.filter((v) => v.lang.includes("en-US"));
  //   const selectedVoice = voiceOptions.length > 0 ? voiceOptions[0] : availableVoices[0];

  //   if (!selectedVoice) {
  //     console.warn("No voice available");
  //     return;
  //   }

  //   window.speechSynthesis.resume();
  //   utterance.voice = selectedVoice;
  //   utterance.volume = 1;
  //   utterance.pitch = 1; // lower = deeper
  //   utterance.rate = 1.1;

  //   utterance.onstart = function () {
  //     dispatch(updateEnvironment({ isVoiceOutputEnabled: true }));
  //   };
  //   utterance.onend = () => {
  //     dispatch(updateEnvironment({ isVoiceOutputEnabled: false }));
  //   };
  //   utterance.onerror = function (event) {
  //     dispatch(updateEnvironment({ isVoiceOutputEnabled: false }));
  //   };
  //   meSpeak.speak(cleanMarkdown(text), { amplitude: 100, pitch: 50, speed: 175 });

  //   synth.speak(utterance);
  // }, [dispatch, availableVoices, synth]);

  /**
   * Author: Satyam Sharma
   * Date: 29 Jan 2025
   * Description: useEffect hook to handle the typing simulation and text-to-speech functionality.
   * It sets an interval to simulate typing by updating the display text character by character.
   * When a sentence-ending punctuation is encountered, it triggers the Web Speech API to speak the accumulated text.
   * The interval is cleared once the entire message is processed or if the component is unmounted.
   */
  useEffect(() => {
    const isStreamingEnabled = environment.isResponseStreamingEnabled;
    const isActiveMessage = (activeMID === message.mid);
    const isStreaming = environment?.ai?.streaming ?? false;

    // Handle non-streaming case
    if (!isStreamingEnabled) {
      setDisplayText(message?.label);
      if (isActiveMessage && isVoiceSupportEnabled && environment.isVoiceOutputEnabled) {
        // speakText(message?.label, SPEAKING_RATE);
      }
      return;
    }

    // Handle non-active or non-streaming messages
    if (!isActiveMessage) {
      setDisplayText(message?.label);
      return;
    }

    // Handle streaming case
    if (isActiveMessage && isStreaming) {
      const typingInterval = setInterval(() => {
        if (currentIndex < message?.label.length) {
          const nextIndex = message?.label.indexOf(' ', currentIndex + 1) + 1 || message?.label.length;
          const newText = message?.label.slice(currentIndex, nextIndex);

          setDisplayText(prev => prev + `${newText}`);
          setCurrentIndex(nextIndex);

          if (isVoiceSupportEnabled && environment.isVoiceOutputEnabled) {
            setAccumulatedText(prev => {
              const updatedText = prev + newText;
              if (/[.!?,:;]/.test(newText)) {
                // speakText(updatedText, SPEAKING_RATE);
                setSpokenText(spokenText + updatedText);
                return '';
              }
              return updatedText;
            });
          }

        } else {

          clearInterval(typingInterval);
          dispatch(updateAiEnvironment({ streaming: false }));
          if (isVoiceSupportEnabled && accumulatedText && environment.isVoiceOutputEnabled) {
            // speakText(accumulatedText, SPEAKING_RATE);
            setSpokenText((prev) => prev + accumulatedText);
            setAccumulatedText('');
          }
          dispatch(updateEnvironment({ isVoiceOutputEnabled: false }));
        }
      }, TYPING_SPEED);

      return () => clearInterval(typingInterval);
    }
  }, [
    message.label,
    currentIndex,
    accumulatedText,
    activeMID,
    // speakText,
    dispatch,
    spokenText,
    setAccumulatedText,
    environment.isResponseStreamingEnabled,
    environment?.ai?.streaming,
    environment?.chatMenu?.voiceSupport?.isEnabled,
    environment.isVoiceOutputEnabled,
    message.mid,
    isVoiceSupportEnabled
  ]);

  const handleLikes = (type: string) => {
    if (type === "thumbsUp") {
      setHasClickedThumbsUp(true);
      setHasClickedThumbsDown(false);
      submitFeedback({ mid: message.mid, hasClickedThumbsUp: true, hasClickedThumbsDown: false })
    } else {
      setHasClickedThumbsUp(false);
      setHasClickedThumbsDown(true);
      submitFeedback({ mid: message.mid, hasClickedThumbsUp: false, hasClickedThumbsDown: true })
    }
  }

  const handleSpectrumClick = () => {
    stopVoiceOutput();
  }

  const handleImageClick = (e: any) => {
    if (e.target.tagName === 'IMG') {
      dispatch(updateEnvironment({
        imagePreview: {
          isActive: true,
          src: e.target.currentSrc
        }
      }));
    }
  }

  return (
    <>
      {
        !!displayText
          ?
          <div className="main-container fade-in ai mb-3">
            <div className="message flex items-end">
              <div className={`rounded-xl py-2 px-4 max-w-80 bg-primaryBgColor relative  ${topMargin}`}>
                <div onClick={handleImageClick} className={`ele-text-message-label prose ${message?._parent ? 'ml-4' : ''}`}
                  dangerouslySetInnerHTML={{ __html: md.render(displayText || '') }} />

                {
                  (message.position !== 'right' && message.shouldFeedbackVisible) && (
                    <div className="text-right mt-1 ele-text-message-feedback">
                      {
                        environment.activeMID === message.mid &&
                        environment?.activeQuestion?.type.toLowerCase() === 'ai' &&
                        environment?.chatMenu?.voiceSupport?.isEnabled &&
                        environment?.isVoiceOutputEnabled &&
                        <div className='inline-block mr-1' onClick={handleSpectrumClick}>
                          <Spectrum></Spectrum>
                        </div>
                      }
                      {
                        hasClickedThumbsUp
                          ?
                          <img onClick={() => handleLikes("thumbsUp")} alt="refresh" className="transition-transform duration-250 transform hover:scale-150 inline-block w-6 cursor-pointer"
                            src={process.env.REACT_APP_CDN + "/thumbs-up.svg"}
                          />
                          :
                          <img onClick={() => handleLikes("thumbsUp")} src={process.env.REACT_APP_CDN + "/thumbs-up-blank.svg"} alt="refresh" className="transition-transform duration-250 transform hover:scale-150 inline-block w-6 cursor-pointer"
                            onMouseEnter={(e) => {
                              (e.target as any).src = process.env.REACT_APP_CDN + "/thumbs-up.svg";
                            }}
                            onMouseLeave={(e) => {
                              (e.target as any).src = process.env.REACT_APP_CDN + "/thumbs-up-blank.svg";
                            }}
                          />

                      }
                      {
                        hasClickedThumbsDown
                          ?
                          <img onClick={() => handleLikes("thumbsDown")} src={process.env.REACT_APP_CDN + "/thumbs-down.svg"} alt="refresh" className="transition-transform duration-250 transform hover:scale-150 inline-block w-6 cursor-pointer" />
                          :
                          <img onClick={() => handleLikes("thumbsDown")} src={process.env.REACT_APP_CDN + "/thumbs-down-blank.svg"} alt="refresh" className="transition-transform duration-250 transform hover:scale-150 inline-block w-6 cursor-pointer"
                            onMouseEnter={(e) => {
                              (e.target as any).src = process.env.REACT_APP_CDN + "/thumbs-down.svg";
                            }}
                            onMouseLeave={(e) => {
                              (e.target as any).src = process.env.REACT_APP_CDN + "/thumbs-down-blank.svg";
                            }} />
                      }
                    </div>
                  )
                }
              </div>
            </div>
          </div>
          :
          <AiThinking></AiThinking>
      }

    </>
  )
}